import { useCallback, type ReactNode } from 'react';
import styles from './Composer.module.css';
import { Button } from './Button.js';
import { VisuallyHidden } from './VisuallyHidden.js';

/**
 * **`Composer`**（§5.5 / §2.2 / §5.4 / §4.3 の 1）。
 *
 * ## 入力は 1 つ（フリーフォーマット）【2026-08-26。判定: 西島】
 *
 * §4.3 の 1 は「**コードと手元での実行結果を上下 2 段で貼る入力**」を定めており、
 * **一度その形で作った**（考え / コード / 実行結果の 3 段）。
 * **西島さんの判断で 1 つに戻した。**
 *
 * **D6 が要求しているのは「学習者が手元で実行して結果を自己申告すること」**であって、
 * **入力欄が分かれていること**ではない。**欄を分けなくても、
 * 学習者はコードも結果も 1 つの本文に書ける**。
 *
 * **代償**: **欄が促さないので、実行結果を書かない学習者が出る。**
 * **その頻度は実データで測る**（`executionReport` の付いた Evidence の割合）。
 *
 * ## 「押せるが保留される」が要点（§2.2 / §2.7）
 *
 * §4.4 は「**学習者が知覚する待ち時間は約 6 秒。この体験を壊さないことが
 * UI 側の最重要要件で、送信保留の見せ方がその全部である**」と書いている。
 * **だから `disabled` を型に持たせない** — 持たせると必ず使われる。
 *
 * ## Enter で送る
 *
 * **`Shift+Enter` で改行**（モックの `.hint`）。
 * **コードの段では Enter を送信にしない** — **改行がコードの意味だから**である。
 */
export type ComposerState = 'ready' | 'pending' | 'streaming' | 'blocked';

export interface ComposerProps {
  readonly value: string;
  readonly onChange: (v: string) => void;
  readonly onSubmit: () => void;
  readonly state: ComposerState;
}

const LABEL: Readonly<Record<ComposerState, string>> = {
  ready: '送信',
  pending: '準備中',
  streaming: '送信',
  blocked: '送信',
};

export function Composer({ value, onChange, onSubmit, state }: ComposerProps): ReactNode {
  /**
   * **Enter で送る。`Shift+Enter` で改行。**
   * **変換中（IME）は送らない** — `isComposing` を見ないと
   * **日本語の確定 Enter で送信される**（日本語入力では致命的）。
   */
  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key !== 'Enter' || e.shiftKey) return;
      if (e.nativeEvent.isComposing) return;
      e.preventDefault();
      onSubmit();
    },
    [onSubmit],
  );

  const blocked = state === 'blocked';

  return (
    <div className={styles['composer']}>
      <div className={styles['box']}>
        <div className={styles['seg']}>
          {/**
           * **ラベルを置かない。** 1 つしか無いので、
           * **「あなたの考え」と書くと「コードは別の場所か」と探させる**。
           * **何を書けばよいかは placeholder が伝える。**
           */}
          <VisuallyHidden>
            <label htmlFor="ua-composer">メッセージ</label>
          </VisuallyHidden>
          <textarea
            id="ua-composer"
            className={styles['input']}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={onKeyDown}
            /**
             * **コードも実行結果も、ここに書いてよいと伝える**（D6）。
             * **欄を分けない代わりに、文で促す。**
             */
            placeholder="わからないところ、書いたコード、手元で動かした結果を、まとめて書いてください"
            rows={4}
            disabled={blocked}
          />
        </div>

        <div className={styles['foot']}>
          <span className={styles['hint']}>Enter で送信 / Shift+Enter で改行</span>
          <Button
            variant="primary"
            shape="pill"
            size="sm"
            /**
             * **`pending` は `disabled` ではない**（§2.7）。
             * 押せて、保留され、`graph_ready` で自動送信される。
             */
            pending={state === 'pending'}
            disabled={blocked}
            onClick={onSubmit}
          >
            {LABEL[state]}
          </Button>
        </div>
      </div>

      {/**
       * **なぜ押せないのかを必ず近くに文字で書く**（§2.7）。
       * **保留していることも言葉で伝える** — ただし
       * **不可視フェーズの存在は明かさない**（§4.4）。
       */}
      {blocked ? (
        <p className={styles['reason']}>
          このセッションは終わりました。「新しい問題」から始められます。
        </p>
      ) : null}
      {state === 'pending' ? (
        <p className={styles['reason']}>準備ができ次第、送信します。</p>
      ) : null}
    </div>
  );
}
